import React from 'react';
import ProfileAssistantRow from '../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../GeneralProfile/ProfileUserAnswerRow';
import { sectionTitleKey, stepQuestionKey } from './hireHealthSurveySteps';
import { userAnswerForStep } from './hireHealthSurveyDisplay';
import type { useHireHealthSurveyFlow } from './useHireHealthSurveyFlow';
import type { HireHealthStep } from './hireHealthSurveyTypes';

type Flow = ReturnType<typeof useHireHealthSurveyFlow>;

export default function HireHealthSurveyChatThread({ flow }: { flow: Flow }) {
  const {
    t,
    step,
    completedSections,
    expandedSectionIndex,
    inProgressVisible,
    showStepPrompt,
    committedStepsInProgress,
    lastCommittedStepInProgress,
    handleUndoInProgress,
    activeSection,
    examDraft,
    textDraft,
    handleUndoSection,
    handleEditSection,
    showEditOnSummary,
    showUndoOnSummary,
    beginEditField,
    threadEndRef,
  } = flow;

  const undoLabel = t('submitDocuments.chat.undo');
  const editLabel = t('submitDocuments.fillFormsChecklist.edit');
  const showFieldEdit = step === 'finalConfirm' && expandedSectionIndex !== null;

  return (
    <div className="gen-profile-chat__thread" role="log" aria-live="polite">
      {completedSections.map((entry, i) => (
        <React.Fragment key={`section-${entry.sectionId}-${i}`}>
          {expandedSectionIndex === i ? (
            <SectionExpanded flow={flow} undoLabel={undoLabel} editLabel={editLabel} showFieldEdit={showFieldEdit} />
          ) : (
            <>
              <ProfileAssistantRow>{t(sectionTitleKey(entry.sectionId))}</ProfileAssistantRow>
              <ProfileUserAnswerRow
                showEdit={showEditOnSummary(i)}
                onEdit={() => handleEditSection(i)}
                showUndo={showUndoOnSummary(i)}
                onUndo={() => handleUndoSection(i)}
                undoLabel={undoLabel}
                editLabel={editLabel}
              >
                {entry.text}
              </ProfileUserAnswerRow>
            </>
          )}
        </React.Fragment>
      ))}

      {inProgressVisible && activeSection && (
        <SectionInProgress
          flow={flow}
          undoLabel={undoLabel}
          editLabel={editLabel}
          committedSteps={committedStepsInProgress}
          lastCommitted={lastCommittedStepInProgress}
          showCurrentPrompt={showStepPrompt}
          onUndoLast={handleUndoInProgress}
        />
      )}

      {step === 'finalConfirm' && expandedSectionIndex === null && (
        <ProfileAssistantRow animateIn>{t('submitDocuments.hireHealthSurvey.finalConfirm')}</ProfileAssistantRow>
      )}

      <div ref={threadEndRef} />
    </div>
  );
}

function SectionInProgress({
  flow,
  undoLabel,
  editLabel,
  committedSteps,
  lastCommitted,
  showCurrentPrompt,
  onUndoLast,
}: {
  flow: Flow;
  undoLabel: string;
  editLabel: string;
  committedSteps: HireHealthStep[];
  lastCommitted: HireHealthStep | undefined;
  showCurrentPrompt: boolean;
  onUndoLast: () => void;
}) {
  const { t, step, activeSection, examDraft, textDraft } = flow;
  if (!activeSection) return null;

  return (
    <>
      <ProfileAssistantRow animateIn>{t(sectionTitleKey(activeSection))}</ProfileAssistantRow>
      {committedSteps.map((committedStep) => (
        <React.Fragment key={`in-progress-${activeSection}-${committedStep}`}>
          <ProfileAssistantRow>{t(stepQuestionKey(committedStep))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={committedStep === lastCommitted}
            onUndo={onUndoLast}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep(committedStep, activeSection, examDraft, textDraft, t)}
          </ProfileUserAnswerRow>
        </React.Fragment>
      ))}
      {showCurrentPrompt && <ProfileAssistantRow animateIn>{t(stepQuestionKey(step))}</ProfileAssistantRow>}
    </>
  );
}

function SectionExpanded({
  flow,
  undoLabel,
  editLabel,
  showFieldEdit,
}: {
  flow: Flow;
  undoLabel: string;
  editLabel: string;
  showFieldEdit: boolean;
}) {
  const { t, step, examDraft, textDraft, beginEditField, handleUndoSection, expandedSectionIndex } = flow;
  const entry = expandedSectionIndex != null ? flow.completedSections[expandedSectionIndex] : null;
  if (!entry) return null;

  const gateStep = `${entry.sectionId}_gate` as HireHealthStep;
  const isExam = entry.snapshot.kind === 'exam';

  const fitLabel =
    examDraft.fit === 'fit'
      ? t('submitDocuments.hireHealthSurvey.fit')
      : examDraft.fit === 'not_fit'
        ? t('submitDocuments.hireHealthSurvey.notFit')
        : '';

  return (
    <>
      <ProfileAssistantRow>{t(sectionTitleKey(entry.sectionId))}</ProfileAssistantRow>
      <ProfileAssistantRow>{t(stepQuestionKey(gateStep))}</ProfileAssistantRow>
      <ProfileUserAnswerRow
        showEdit={showFieldEdit}
        onEdit={() => beginEditField(gateStep)}
        undoLabel={undoLabel}
        editLabel={editLabel}
      >
        {isExam
          ? examDraft.hadExam === 'yes'
            ? t('submitDocuments.hireHealthSurvey.yes')
            : t('submitDocuments.hireHealthSurvey.no')
          : textDraft.hadIssue === 'yes'
            ? t('submitDocuments.hireHealthSurvey.yes')
            : entry.sectionId === 'symptoms'
              ? t('submitDocuments.hireHealthSurvey.noSymptoms')
              : t('submitDocuments.hireHealthSurvey.no')}
      </ProfileUserAnswerRow>

      {isExam && examDraft.hadExam === 'yes' && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey(`${entry.sectionId}_date` as HireHealthStep))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showEdit={showFieldEdit}
            onEdit={() => beginEditField(`${entry.sectionId}_date` as HireHealthStep)}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {examDraft.examDate ? examDraft.examDate.replace(/-/g, '/') : '—'}
          </ProfileUserAnswerRow>
          <ProfileAssistantRow>{t(stepQuestionKey(`${entry.sectionId}_hospital` as HireHealthStep))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showEdit={showFieldEdit}
            onEdit={() => beginEditField(`${entry.sectionId}_hospital` as HireHealthStep)}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {examDraft.hospital.trim() || '—'}
          </ProfileUserAnswerRow>
          <ProfileAssistantRow>{t(stepQuestionKey(`${entry.sectionId}_fit` as HireHealthStep))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showEdit={showFieldEdit}
            onEdit={() => beginEditField(`${entry.sectionId}_fit` as HireHealthStep)}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {fitLabel || '—'}
          </ProfileUserAnswerRow>
        </>
      )}

      {!isExam && textDraft.hadIssue === 'yes' && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey(`${entry.sectionId}_detail` as HireHealthStep))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showEdit={showFieldEdit}
            onEdit={() => beginEditField(`${entry.sectionId}_detail` as HireHealthStep)}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {textDraft.detail.trim() || '—'}
          </ProfileUserAnswerRow>
        </>
      )}
    </>
  );
}
